import { mentionText } from '../core/people-mentions';
import { actionAvailability, actionsInDraft, appointmentWhenFrom, extraActionsNotice, recentFirst, taskDueFrom, withoutWhen } from '../core/actions';
import type { ChatAction } from '../core/actions';
import type { ActionCard, ConversationType, MessageView, PatientMention } from '../core/hub-types';

/** "/task" as a word of its own: at the start or after a space, and followed by a space or the end. */
const wordOf = (label: string, flags = '') =>
  new RegExp(`(^|\\s)${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, flags);

/**
 * What a chat action (`/task`) carries to its page: the draft without the action word and without the `#patient` it
 * is for (the page is already that patient's), with the spaces left behind folded.
 */
export function actionDraftText(draft: string, label: string | readonly string[], dropTexts: readonly string[]): string {
  let text = draft;
  for (const t of dropTexts) if (t) text = text.split(t).join(' ');
  for (const l of typeof label === 'string' ? [label] : label) text = text.replace(wordOf(l, 'g'), ' ');
  return text.replace(/\s+/g, ' ').trim();
}

/** The `/ Action` chip: `/` goes after the draft as the @ and # chips type theirs (people-mentions.ts withTrigger). */
export function withSlash(text: string): string {
  return !text || /\s$/.test(text) ? `${text}/` : `${text} /`;
}

/**
 * The action a draft runs: the first one typed whose word ("/task") stands on its own, and, when it names others too,
 * what to tell the person (one action runs per message). Null when it names none.
 */
export function draftRun(draft: string, actions: readonly ChatAction[]): { action: ChatAction; notice: string | null } | null {
  const [action, ...others] = actionsInDraft(draft, actions);
  return action ? { action, notice: extraActionsNotice(action, others) } : null;
}

/**
 * Where a card's Open goes: the record's page, by way of its patient's file (as the action that made it), in the
 * conversation's practice (its clinical groupId) when known.
 */
export function actionCardPath(card: Pick<ActionCard, 'recordId' | 'patientRef'> & Partial<Pick<ActionCard, 'type'>>, practice?: string | null): string {
  const page = card.type === 'appointment' ? 'edit-appointment' : card.type === 'note' ? 'edit-note' : 'edit-task';
  const where = practice ? `&practice=${encodeURIComponent(practice)}` : '';
  return `/patients/(patient:file/${encodeURIComponent(card.patientRef)})?action=${page}&record=${encodeURIComponent(card.recordId)}${where}`;
}

/** What `/appointment` carries to the booking page: the day and time its text names, and the rest as the reason. */
export function bookingPrefill(text: string, today: string): { text: string; date?: string; time?: string } {
  return { text: withoutWhen(text), ...appointmentWhenFrom(text, today) };
}

/** A booking card's buttons: Confirm while booked, Cancel while booked or confirmed. */
export function bookingButtons(card: ActionCard): { confirm: boolean; cancel: boolean } {
  if (card.type !== 'appointment') return { confirm: false, cancel: false };
  const status = card.status ?? 'booked';
  return { confirm: status === 'booked', cancel: status === 'booked' || status === 'confirmed' };
}

/** Where a card stands, for the colour of its outline (as RelayMD's cardState reads it). */
export type CardState = 'open' | 'due_today' | 'overdue' | 'done' | 'booked' | 'confirmed' | 'arrived' | 'completed' | 'cancelled' | 'did_not_attend' | 'note';

export function cardState(card: ActionCard, today: string): CardState {
  if (card.type === 'note') return 'note';
  if (card.type === 'appointment') {
    const s = card.status;
    return s === 'confirmed' || s === 'arrived' || s === 'completed' || s === 'cancelled' || s === 'did_not_attend' ? s : 'booked';
  }
  if (card.status === 'done') return 'done';
  if (!card.due) return 'open';
  return card.due < today ? 'overdue' : card.due === today ? 'due_today' : 'open';
}

/** The outline (and icon) colour of each state: the same hexes RelayMD draws. */
const OUTLINES: Record<CardState, string> = {
  open: '#0085B6', booked: '#0085B6', due_today: '#E5A100', overdue: '#E5484D', done: '#2E9E6B', confirmed: '#2E9E6B',
  arrived: '#14A3A3', completed: '#8A939C', cancelled: '#B0636A', did_not_attend: '#C05621', note: '#6D5BD0',
};

export function cardOutline(state: CardState): string {
  return OUTLINES[state];
}

/** What a card's own menu offers: the record's actions first, then what a card can do as a message. */
export interface CardActions {
  open: boolean;
  /** an open task; it acts on the record, so a chat that cannot be written to still allows it */
  markDone: boolean;
  /** a booking still only booked */
  confirm: boolean;
  /** a booking booked or confirmed */
  cancel: boolean;
  reply: boolean;
  pin: boolean;
  /** who read it: the author's own card, where receipts are shown */
  info: boolean;
  /** the author's own card; its copy in a patient's chat belongs to the record and stays */
  delete: boolean;
}

export function cardActions(
  m: Pick<MessageView, 'kind' | 'sender' | 'deletedAt' | 'card'> & { pending?: boolean },
  ctx: { me: string | null; writable: boolean; receipts: boolean; support: boolean },
): CardActions {
  const card = m.card;
  const live = !m.pending && !m.deletedAt && !!card;
  const own = live && m.sender?.principalId === ctx.me && m.kind === 'action';
  const booking = bookingButtons(card ?? ({ type: 'task' } as ActionCard));
  return {
    open: live,
    markDone: live && card!.type === 'task' && card!.status !== 'done',
    confirm: live && booking.confirm,
    cancel: live && booking.cancel,
    reply: live && ctx.writable,
    pin: live && ctx.writable,
    info: own && ctx.receipts,
    delete: own && ctx.writable && !ctx.support,
  };
}

/** The time of day on this device, HH:mm: how a note from the chat says when each message was sent. */
export function localClock(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Today on this device, YYYY-MM-DD: what due dates are read from and card lines measured against. */
export function localDay(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The due day the text names and the clinical user ids of the @people, for the action's page. The hub's mention list
 * carries `clinicalUserId` for colleagues with a clinical login; anyone without one is left out.
 */
export function actionPrefillExtras(
  text: string,
  today: string,
  principalIds: readonly string[],
  people: readonly { principalId: string; clinicalUserId?: string }[],
): { due?: string; assignees?: string[] } {
  const out: { due?: string; assignees?: string[] } = {};
  const due = taskDueFrom(text, today);
  if (due) out.due = due;
  const ids: string[] = [];
  for (const principalId of principalIds) {
    const id = people.find((p) => p.principalId === principalId)?.clinicalUserId;
    if (id && !ids.includes(id)) ids.push(id);
  }
  if (ids.length) out.assignees = ids;
  return out;
}

export interface ActionTile<A extends ChatAction = ChatAction> {
  action: A;
  available: boolean;
  reason: string | null;
}

/** The action sheet: every action, recently used first, each greyed with the reason when it cannot run here. */
export function actionSheetTiles<A extends ChatAction>(actions: readonly A[], conversation: { type: ConversationType }, recentIds: readonly string[]): ActionTile<A>[] {
  return recentFirst(actions, recentIds).map((action) => {
    const a = actionAvailability(action, conversation);
    return { action, available: a.available, reason: a.available ? null : a.reason };
  });
}

type RecentStore = Pick<Storage, 'getItem' | 'setItem'>;
const recentKey = (me: string) => `chat.recentActions.${me}`;

/** The actions this person used last on this device, most recent first; empty when storage is missing or refuses. */
export function loadRecentActions(storage: RecentStore | null, me: string): string[] {
  try {
    const list = JSON.parse(storage?.getItem(recentKey(me)) ?? '[]');
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function saveRecentActions(storage: RecentStore | null, me: string, ids: readonly string[]): void {
  try {
    storage?.setItem(recentKey(me), JSON.stringify(ids));
  } catch {
    // A private window or blocked storage: the sheet keeps its usual order.
  }
}

/** The action sheet's pick: the action word goes after the draft, with a space for the text to follow. */
export function withAction(draft: string, label: string): string {
  return `${withSlash(draft).slice(0, -1)}${label} `;
}

/**
 * The patients a message is about, for "Make a task" from it: the patient chats it was linked into, then the patients
 * it names (`titleOf` gives a name the chat knows), once each.
 */
export function messageActionPatients(
  m: Pick<MessageView, 'mentions'> & Partial<Pick<MessageView, 'linkedTo'>>,
  titleOf: (patientRef: string) => string | null,
): PatientMention[] {
  const out: PatientMention[] = [];
  for (const l of m.linkedTo ?? []) {
    if (l.patientRef && l.kind !== 'referral' && !out.some((p) => p.patientRef === l.patientRef)) {
      out.push({ patientRef: l.patientRef, title: l.name ?? titleOf(l.patientRef) ?? 'this patient' });
    }
  }
  for (const ref of m.mentions?.patients ?? []) {
    if (!out.some((p) => p.patientRef === ref)) out.push({ patientRef: ref, title: titleOf(ref) ?? 'this patient' });
  }
  return out;
}

/** The task's text without the @names of the colleagues it is assigned to (they are the assignees now), spaces folded. */
export function withoutAssignees(
  text: string,
  principalIds: readonly string[],
  people: readonly { principalId: string; name: string; clinicalUserId?: string }[],
): string {
  let out = text;
  for (const principalId of principalIds) {
    const person = people.find((p) => p.principalId === principalId && p.clinicalUserId);
    if (person) out = out.split(mentionText(person.name)).join(' ');
  }
  return out.replace(/\s+/g, ' ').trim();
}
